// Maps AMA Guides (5th Edition) Table/Figure references to their page number.
// Source: RatingReferenceGuide doc, cross-checked against the indexed PDF
// via OpenSearch keyword search and the per-page markdown under
// ~/Desktop/RAG/ama_pdf_text. When the two disagree, the actual PDF page
// (as confirmed by the definition-style heading in the extracted text) wins.

export const RATING_REFERENCE_PAGES: Record<string, number> = {
  // Spine (Chapter 15)
  "table-15-2": 376,
  "table-15-3": 384,
  "table-15-4": 389,
  "table-15-5": 392,

  // Upper extremity (Chapter 16)
  "table-16-1": 438,
  "table-16-2": 439,
  "table-16-3": 439,
  "table-16-4": 440,
  "table-16-5": 447,
  "table-16-6": 448,
  "table-16-7": 448,
  "table-16-9": 460,
  "table-16-17": 498,
  "figure-16-2": 441,
  "figure-16-3": 442,
  "figure-16-4": 443,
  "figure-16-5": 443,
  "figure-16-6": 447,
  "figure-16-7": 447,
  "figure-16-8": 449,
  "figure-16-9": 451,
  "figure-16-10": 455,
  "figure-16-11": 455,
  "figure-16-12": 456,
  "figure-16-13": 456,
  "figure-16-14": 457,
  "figure-16-15": 457,
  "figure-16-16": 458,
  "figure-16-17": 458,
  "figure-16-18": 459,
  "figure-16-19": 460,
  "figure-16-20": 461,
  "figure-16-21": 461,
  "figure-16-22": 463,
  "figure-16-23": 463,
  "figure-16-24": 464,
  "figure-16-25": 464,
  "figure-16-26": 466,
  "figure-16-27": 467,
  "figure-16-28": 467,
  "figure-16-29": 468,
  "figure-16-30": 469,
  "figure-16-31": 469,
  "figure-16-32": 471,
  "figure-16-33": 471,
  "figure-16-34": 472,
  "figure-16-35": 473,
  "figure-16-36": 473,
  "figure-16-37": 474,
  "figure-16-38": 475,
  "figure-16-39": 475,
  "figure-16-40": 476,
  "figure-16-41": 477,
  "figure-16-42": 477,
  "figure-16-43": 477,
  "figure-16-44": 478,
  "figure-16-45": 478,

  // Lower extremity (Chapter 17)
  "table-17-2": 526,
  "table-17-3": 527,
  "table-17-4": 528,
  "table-17-5": 529,
  "table-17-6": 530,
  "table-17-7": 531,
  "table-17-8": 532,
  "table-17-9": 537,
  "table-17-10": 537,
  "table-17-11": 537,
  "table-17-12": 537,
  "table-17-13": 537,
  "table-17-14": 537,
  "table-17-15": 538,
  "table-17-16": 539,
  "table-17-17": 539,
  "table-17-18": 539,
  "table-17-19": 539,
  "table-17-20": 540,
  "table-17-21": 540,
  "table-17-22": 540,
  "table-17-23": 540,
  "table-17-24": 541,
  "table-17-25": 541,
  "table-17-26": 541,
  "table-17-27": 541,
  "table-17-28": 541,
  "table-17-29": 542,
  "table-17-30": 543,
  "table-17-31": 544,
  "table-17-32": 545,
  "table-17-33": 546,
  "table-17-34": 548,
  "table-17-35": 549,
  "table-17-36": 550,
  "table-17-37": 552,
  "table-17-38": 554,
  "figure-17-1": 534,
  "figure-17-2": 534,
  "figure-17-3": 535,
  "figure-17-4": 535,
  "figure-17-5": 535,
  "figure-17-6": 536,
  "figure-17-8": 551,
  "figure-17-9": 551,
};

// Any dash-like separator the AMA text (or our linkifier output) might use:
// ASCII hyphen-minus, Unicode hyphen, non-breaking hyphen, en/em dash, and
// the minus sign. Centralized here so linkification, parsing, and the anchor
// id all agree.
const DASH_CHARS = "-‐‑–—−";
const DASH_RE = new RegExp(`[${DASH_CHARS}]`, "g");
const normalizeDashes = (s: string): string => s.replace(DASH_RE, "-");

// The AMA Guides 5th ed. has 18 chapters, so a "section" whose chapter part
// falls outside that range is not one of ours — most often a statute the
// rating text cites in the same shape (CA Labor Code 4660.1). Those must not
// linkify: a search for "4660.1" in the AMA PDF can only fail.
const AMA_CHAPTER_COUNT = 18;

const isAmaChapter = (chapter: string): boolean => {
  const n = Number(chapter);
  return Number.isInteger(n) && n >= 1 && n <= AMA_CHAPTER_COUNT;
};

// Extract "table-16-3", "figure-16-40" or "ch-17-2k" from an href like
// "#table-16-3" / "#ch-17-2k", or from a raw string like "Table 16-3" /
// "Section 17.2k". Returns null if the shape doesn't match.
export function parseRatingReferenceId(
  input: string | undefined,
): string | null {
  if (!input) return null;
  const cleaned = normalizeDashes(input.replace(/^#/, "").trim().toLowerCase());
  const match = cleaned.match(/^(table|figure)[\s-]+(\d+-\d+)$/);
  if (match) return `${match[1]}-${match[2]}`;
  // Sections arrive as the canonical id ("ch-17-2k"), as rendered prose
  // ("Section 17.2k"), or — from results stored before the backend settled
  // on that form — as "Ch. 17.2k" or the raw section glyph ("§17.2k").
  const section = cleaned.match(
    /^(?:section\s*|ch[\s.-]*|§\s*)(\d+)[.-](\d+[a-z]?)$/,
  );
  if (section && isAmaChapter(section[1])) {
    return `ch-${section[1]}-${section[2]}`;
  }
  return null;
}

/**
 * The term to search the AMA Guide PDF for, given a canonical reference id.
 *
 * Tables and figures are titled in the book exactly as we render them
 * ("Table 16-3"), so the label doubles as the query. Section headings are
 * not — the book prints a bare "17.2k", never "Section 17.2k" — so the
 * "Section" prefix we show the reader is dropped before searching. Both surfaces
 * derive the query here rather than from the visible link text, so the
 * two can differ without either one guessing.
 */
export function ratingReferenceSearchTerm(id: string): string | null {
  const section = id.match(/^ch-(\d+)-(\d+[a-z]?)$/);
  if (section) return `${section[1]}.${section[2]}`;
  const ref = id.match(/^(table|figure)-(\d+-\d+)$/);
  if (!ref) return null;
  const kind = ref[1][0].toUpperCase() + ref[1].slice(1);
  return `${kind} ${ref[2]}`;
}

export function lookupRatingReferencePage(
  input: string | undefined,
): number | null {
  const id = parseRatingReferenceId(input);
  if (!id) return null;
  return RATING_REFERENCE_PAGES[id] ?? null;
}

// Matches a bare AMA Guides reference like "Table 16-3" or "Figure 17-5".
// Requires the chapter-index form with a dash-like separator so "Table 5" or
// "Figure 3" (unrelated prose) don't match. The AMA PDF and LLM output use a
// mix of ASCII hyphen-minus and Unicode hyphens/dashes, so we accept all of
// them and normalize to "-" in the generated link text + href.
//
// Negative lookbehind `(?<!\[)` skips matches that are already inside a
// markdown link's text `[Table 16-3](...)` — guards against double-linking
// if any upstream content ever arrives pre-linked.
const REFERENCE_RE = new RegExp(
  `(?<!\\[)\\b(Table|Figure)\\s+(\\d+[${DASH_CHARS}]\\d+)\\b`,
  "gi",
);

// Matches an AMA section citation: "Section 17.2k" as the report now renders
// it, plus the "Ch. 17.2k" and raw "§17.2k" forms still carried by results
// stored before the backend settled on that wording. Requires the
// chapter.section shape so a bare "Section 17" (or any stray decimal in prose)
// doesn't match, and the chapter number is range-checked in the replacer so
// statute citations like "Section 4660.1" stay plain text.
const SECTION_RE =
  /(?<!\[)(?:\bSection\s+|\bCh\.?\s*|§\s*)(\d+)\.(\d+[a-z]?)\b/g;

/**
 * Rewrite plain "Table X-Y" / "Figure X-Y" / "Section X.Yz" mentions in the
 * impairment rating markdown into links pointing at their canonical anchor
 * (e.g. `#table-16-3`, `#ch-17-2k`). The rating-format prompt emits plain
 * prose only, so we don't guard against already-formatted links or code spans.
 */
export function linkifyRatingReferences(markdown: string): string {
  if (!markdown) return markdown;
  const withRefs = markdown.replace(
    REFERENCE_RE,
    (_, kind: string, num: string) => {
      const Kind = kind[0].toUpperCase() + kind.slice(1).toLowerCase();
      // Normalize the dash so link text, anchor href, and the static mapping
      // keys all stay consistent (`table-16-12`, not `table-16‐12`).
      const normalizedNum = normalizeDashes(num);
      return `[${Kind} ${normalizedNum}](#${Kind.toLowerCase()}-${normalizedNum})`;
    },
  );
  return withRefs.replace(SECTION_RE, (match, chapter: string, rest: string) =>
    isAmaChapter(chapter)
      ? // Always render the "Section" form, whichever spelling arrived — a
        // stored result's "§17.2k" links and reads the same as a fresh one.
        `[Section ${chapter}.${rest}](#ch-${chapter}-${rest})`
      : match,
  );
}
